/**
 * Honorarios — Otros cobros (trabajos extraordinarios: monto + motivo).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DespachoSectionCard } from "@/components/despacho/DespachoUi";
import {
  honorariosApi,
  parseHonorariosError,
} from "@/modules/despacho/despachoHonorariosApi";

function money(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}

const EMPTY = {
  fiscal_client_id: "",
  amount: "",
  reason: "",
  charge_date: "",
  notes: "",
};

export default function HonorariosOtrosCobrosPanel({
  clients = [],
  readOnly = false,
  canViewAmounts = true,
}) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [onlyPending, setOnlyPending] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await honorariosApi.listExtraCharges({ onlyPending });
      setItems(data.items || []);
      setSummary(data.summary || {});
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setLoading(false);
    }
  }, [onlyPending]);

  useEffect(() => {
    load();
  }, [load]);

  const clientOptions = useMemo(() => clients || [], [clients]);

  const openCreate = () => {
    setEditingId("");
    setForm({ ...EMPTY, charge_date: new Date().toISOString().slice(0, 10) });
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      fiscal_client_id: row.fiscal_client_id,
      amount: String(row.amount ?? ""),
      reason: row.reason || "",
      charge_date: row.charge_date || "",
      notes: row.notes || "",
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (readOnly) return;
    if (!form.fiscal_client_id) {
      toast.error("Seleccione un cliente.");
      return;
    }
    if (!form.reason.trim()) {
      toast.error("Indique el motivo.");
      return;
    }
    setBusy(true);
    try {
      if (editingId) {
        await honorariosApi.updateExtraCharge(editingId, {
          amount: Number(form.amount || 0),
          reason: form.reason.trim(),
          charge_date: form.charge_date,
          notes: form.notes,
        });
      } else {
        const c = clientOptions.find((x) => String(x.fiscal_client_id) === String(form.fiscal_client_id));
        await honorariosApi.createExtraCharge({
          fiscal_client_id: form.fiscal_client_id,
          amount: Number(form.amount || 0),
          reason: form.reason.trim(),
          charge_date: form.charge_date,
          client_name: c?.name || "",
          client_rfc: c?.rfc || "",
          notes: form.notes,
        });
      }
      toast.success("Guardado.");
      setFormOpen(false);
      await load();
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const toggleCollected = async (row) => {
    if (readOnly) return;
    try {
      await honorariosApi.updateExtraCharge(row.id, { collected: !row.collected });
      await load();
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    }
  };

  const handleDelete = async (row) => {
    if (readOnly) return;
    if (!window.confirm("¿Eliminar este cobro?")) return;
    try {
      await honorariosApi.deleteExtraCharge(row.id);
      toast.success("Eliminado.");
      await load();
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    }
  };

  return (
    <div className="space-y-3" data-testid="honorarios-otros-cobros">
      <DespachoSectionCard title="Otros cobros">
        <p className="m-0 mb-3 text-[11px] text-slate-500">
          Trabajos extraordinarios al cliente: monto y motivo. Sirve para reportes de honorarios +
          eventuales + extras.
        </p>
        <div className="flex flex-wrap gap-2 mb-3 items-center text-xs">
          {!readOnly ? (
            <Button type="button" size="sm" className="rounded-sm h-8 text-xs bg-[#0F2D5C]" onClick={openCreate}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Agregar cobro
            </Button>
          ) : null}
          <label className="flex items-center gap-1.5 text-slate-600">
            <Checkbox checked={onlyPending} onCheckedChange={(v) => setOnlyPending(Boolean(v))} />
            Solo pendientes
          </label>
          <span className="text-slate-500">
            {summary.count ?? 0} registro(s)
            {canViewAmounts ? ` · Total ${money(summary.total)} · Pendiente ${money(summary.pending_total)}` : ""}
          </span>
        </div>

        {formOpen ? (
          <div className="mb-3 rounded-sm border border-slate-200 bg-slate-50 p-3 space-y-2">
            <div className="grid gap-2 sm:grid-cols-2">
              {!editingId ? (
                <div className="sm:col-span-2">
                  <Label className="text-xs">Cliente</Label>
                  <Select
                    value={form.fiscal_client_id || undefined}
                    onValueChange={(v) => setForm((f) => ({ ...f, fiscal_client_id: v }))}
                  >
                    <SelectTrigger className="mt-1 h-8 text-xs rounded-sm">
                      <SelectValue placeholder="Seleccione…" />
                    </SelectTrigger>
                    <SelectContent>
                      {clientOptions.map((c) => (
                        <SelectItem key={c.fiscal_client_id} value={c.fiscal_client_id}>
                          {c.name} {c.rfc ? `· ${c.rfc}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              <div>
                <Label className="text-xs">Monto</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1 h-8 text-xs"
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Fecha</Label>
                <Input
                  type="date"
                  className="mt-1 h-8 text-xs"
                  value={form.charge_date}
                  onChange={(e) => setForm((f) => ({ ...f, charge_date: e.target.value }))}
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Motivo</Label>
                <Input
                  className="mt-1 h-8 text-xs"
                  value={form.reason}
                  onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                  placeholder="Ej. Declaración complementaria / auditoría extraordinaria"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="button" size="sm" className="h-8 text-xs bg-[#0F2D5C]" disabled={busy} onClick={handleSave}>
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Guardar"}
              </Button>
              <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setFormOpen(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        ) : (
          <div className="overflow-x-auto rounded-sm border border-slate-200">
            <table className="w-full text-[11px] min-w-[640px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left">
                  <th className="p-2 font-medium">Fecha</th>
                  <th className="p-2 font-medium">Cliente</th>
                  <th className="p-2 font-medium">Motivo</th>
                  <th className="p-2 font-medium">Monto</th>
                  <th className="p-2 font-medium">Cobrado</th>
                  <th className="p-2 font-medium w-24">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.length ? items.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100 last:border-0" data-testid={`extra-charge-${row.id}`}>
                    <td className="p-2 whitespace-nowrap">{row.charge_date || "—"}</td>
                    <td className="p-2">
                      <span className="font-medium">{row.client_name || "—"}</span>
                      {row.client_rfc ? <span className="block text-slate-500 font-mono">{row.client_rfc}</span> : null}
                    </td>
                    <td className="p-2">{row.reason || "—"}</td>
                    <td className="p-2 whitespace-nowrap">{canViewAmounts ? money(row.amount) : "•••"}</td>
                    <td className="p-2">
                      <Checkbox
                        checked={Boolean(row.collected)}
                        disabled={readOnly}
                        onCheckedChange={() => toggleCollected(row)}
                      />
                    </td>
                    <td className="p-2">
                      {!readOnly ? (
                        <div className="flex gap-1">
                          <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(row)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-700" onClick={() => handleDelete(row)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={6} className="p-3 text-slate-500">Sin otros cobros registrados.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </DespachoSectionCard>
    </div>
  );
}
